<script setup lang="ts">
/*
* Компонент DateTimePicker - кастомный компонент выбора даты и времени с интерактивными скроллерами
*
* Основные функции:
* - Выбор даты через VDatePicker и времени через кастомные скроллеры
* - Плавная инерционная прокрутка часов и минут с "прилипанием" к значениям
* - Поддержка touch и mouse взаимодействия для мобильных и десктопных устройств
* - Циклическая прокрутка с бесконечным скроллингом в обе стороны
*
* Особенности реализации:
* - Кастомная физика прокрутки с анимацией "прилипания"
* - Единый набор DOM элементов с визуальной репликацией для циклического эффекта
* - Динамическое чтение CSS переменных для гибкости стилизации
* - Адаптивный дизайн с различной компоновкой на мобильных устройствах
*/

// Импорты Vue
import { computed, nextTick, onMounted, ref, watch } from 'vue'

// Импорты Vuetify
import { useDisplay } from 'vuetify/framework'

// Определение интерфейсов для props и emits
interface Props {
  modelValue: Date | null // Текущее значение даты и времени (v-model)
  minuteStep?: number // Шаг минут в селекторе (по умолчанию 5 минут)
  onlyPastDate?: boolean // Разрешен выбор только прошлый дат
  onlyFutureDate?: boolean // Разрешен выбор только будущих дат
}
interface Emits {
  (e: 'update:modelValue', value: Date | null): void // Событие обновления v-model
  (e: 'save', value: Date | null): void // Событие сохранения выбора
}

// Инициализация props с значениями по умолчанию
const props = withDefaults(defineProps<Props>(), {
  minuteStep: 5,
  onlyPastDate: false,
  onlyFutureDate: false,
})

/**
 * Определение событий компонента
 *
 * События:
 * - 'update:modelValue': событие обновления значения модели (для v-model)
 *   - Назначение: синхронизация выбранной даты и времени с родительским компонентом
 *   - Параметры: Date | null - выбранный объект даты или null
 *
 * - 'save': событие сохранения выбора
 *   - Назначение: подтверждение выбора даты и времени
 *   - Параметры: Date | null - финальный выбранный объект даты
 */
const emit = defineEmits<Emits>()

/**
 * Вычисляемое значение текущей даты в формате ISO (YYYY-MM-DD)
 *
 * Назначение:
 * - Используется как опорная дата «сегодня» для ограничений выбора в VDatePicker
 * - Формат соответствует ожидаемому формату Vuetify (`YYYY-MM-DD`)
 *
 * Примечание:
 * - Время отбрасывается намеренно, работа ведётся строго на уровне даты
 */
const todayIso = computed(() =>
  new Date().toISOString().substring(0, 10),
)

/**
 * Минимально допустимая дата для выбора
 *
 * Назначение:
 * - Ограничивает выбор дат только будущими (и сегодняшней датой)
 * - Используется для передачи в prop `min` компонента VDatePicker
 *
 * Логика:
 * - Если `onlyFutureDate === true` → минимальная дата = сегодня
 * - Если `onlyFutureDate === false` → ограничение отсутствует
 */
const minDate = computed(() =>
  props.onlyFutureDate ? todayIso.value : undefined,
)

/**
 * Максимально допустимая дата для выбора
 *
 * Назначение:
 * - Ограничивает выбор дат только прошлыми (и сегодняшней датой)
 * - Используется для передачи в prop `max` компонента VDatePicker
 *
 * Логика:
 * - Если `onlyPastDate === true` → максимальная дата = сегодня
 * - Если `onlyPastDate === false` → ограничение отсутствует
 */
const maxDate = computed(() =>
  props.onlyPastDate ? todayIso.value : undefined,
)

// Инициализация утилит отображения Vuetify
const { xs } = useDisplay()

// Референсы DOM элементов
// Референс контейнера скроллера часов
const hoursContainer = ref<HTMLElement | null>(null)

// Референс контейнера скроллера минут
const minutesContainer = ref<HTMLElement | null>(null)

// Реактивные состояния компонента
// Выбранная дата (объект Date)
const selectedDate = ref<Date>(props.modelValue ? new Date(props.modelValue) : new Date())

// Выбранный час (0-23)
const selectedHour = ref<number>(props.modelValue ? props.modelValue.getHours() : new Date().getHours())

// Выбранная минута (с учетом шага)
const selectedMinute = ref<number>(props.modelValue ? props.modelValue.getMinutes() : Math.floor(new Date().getMinutes() / props.minuteStep) * props.minuteStep)

// Массив доступных часов (0-23)
const hours = ref<number[]>(Array.from({ length: 24 }, (_, i) => i))

// Массив доступных минут (с учетом шага)
const minutes = ref<number[]>(
  Array.from({ length: Math.floor(60 / props.minuteStep) }, (_, i) => i * props.minuteStep),
)

// Константы массивов для переиспользования (вместо создания в каждом обработчике)
const HOURS_ARRAY = Array.from({ length: 24 }, (_, i) => i)
const MINUTES_ARRAY = Array.from({ length: Math.floor(60 / props.minuteStep) }, (_, i) => i * props.minuteStep)

// Смещение скроллера часов в пикселях
const hoursOffset = ref(0)

// Смещение скроллера минут в пикселях
const minutesOffset = ref(0)

// Переменные для управления жестами и таймерами
// Высота одной опции в пикселях (читается из CSS)
let OPTION_HEIGHT = 40
const SNAP_DEBOUNCE = 120

// Таймер дебаунса для скроллера часов
let hoursWheelTimer: ReturnType<typeof setTimeout> | null = null

// Таймер дебаунса для скроллера минут
let minutesWheelTimer: ReturnType<typeof setTimeout> | null = null

// Флаг активного жеста перетаскивания для часов и последняя Y координата для жеста часов
let hPointerActive = false; let hLastY = 0

// Флаг активного жеста перетаскивания для минут и последняя Y координата для жеста минут
let mPointerActive = false; let mLastY = 0

/**
 * Чтение CSS переменных из DOM
 *
 * Назначение:
 * - Динамическое получение стилевых параметров для расчетов позиционирования
 * - Обеспечение гибкости стилизации без изменения JavaScript кода
 *
 * Входные данные:
 * - el: HTMLElement | null - элемент из которого читать переменные
 */
const readCssVars = (el: HTMLElement | null) => {
  if (!el) {
    return
  }
  const s = getComputedStyle(el)
  const oh = s.getPropertyValue('--option-height')?.trim() || ''
  if (oh) {
    OPTION_HEIGHT = Number.parseFloat(oh)
  }
}

/**
 * Расчет смещения центра контейнера
 *
 * Назначение:
 * - Определение вертикального смещения для центрирования активной опции
 * - Учет высоты контейнера и высоты опции для точного позиционирования
 *
 * Входные данные:
 * - container: HTMLElement | null - контейнер скроллера
 *
 * Возвращает:
 * - number: смещение в пикселях для центрирования
 */
const containerCenterOffset = (container: HTMLElement | null) => {
  if (!container) {
    return 0
  }
  return (container.clientHeight / 2) - (OPTION_HEIGHT / 2)
}

/**
 * Нормализация смещения скроллера в диапазон средней копии
 *
 * Назначение:
 * - Обеспечение бесконечной циклической прокрутки ("барабан" как на iPhone)
 * - Мгновенный перенос offset в диапазон средней (2-й из 3-х) копии массива
 * - Визуально незаметен, т.к. позиция в цикле не меняется
 *
 * Входные данные:
 * - offsetRef: { value: number } - реактивная ссылка на смещение
 * - arrayLength: number - длина базового массива (24 для часов, кол-во шагов для минут)
 */
function normalizeOffsetOnly(offsetRef: { value: number }, arrayLength: number) {
  const cycleLength = arrayLength * OPTION_HEIGHT

  // Переносим offset в диапазон средней копии через modulo
  // Средняя копия: от -cycleLength до -2*cycleLength
  // Используем остаток от деления, затем сдвигаем в нужный диапазон
  let val = offsetRef.value
  const remainder = ((val % cycleLength) + cycleLength) % cycleLength

  val = remainder - cycleLength
  offsetRef.value = val
}

/**
 * Вычисление выбранного значения на основе смещения
 *
 * Назначение:
 * - Преобразование пиксельного смещения в соответствующее значение часа/минуты
 * - Учет циклической природы данных и центрирования контейнера
 *
 * Входные данные:
 * - offset: number - текущее смещение в пикселях
 * - container: HTMLElement | null - контейнер скроллера
 * - baseArray: number[] - базовый массив значений
 * - type: 'hours' | 'minutes' - тип значения для вычисления
 *
 * Возвращает:
 * - number: вычисленное значение часа или минуты
 */
function computeSelectedValueFromOffset(offset: number, container: HTMLElement | null, baseArray: number[], type: 'hours' | 'minutes') {
  const center = containerCenterOffset(container)
  const rawIndex = Math.round((-offset + center) / OPTION_HEIGHT)
  const listLen = baseArray.length
  const normalizedIndex = ((rawIndex % listLen) + listLen) % listLen
  return baseArray[normalizedIndex]
}

/**
 * Обработчик колесика мыши для скроллера часов
 *
 * Назначение:
 * - Обработка wheel событий с дебаунсом и инерционной прокруткой
 * - Преобразование deltaY в пиксельное смещение
 * - Запуск таймера для финального "прилипания"
 *
 * Входные данные:
 * - e: WheelEvent - событие колесика мыши
 */
const onHoursWheel = (e: WheelEvent) => {
  e.preventDefault()

  const abs = Math.abs(e.deltaY)
  const ONE = OPTION_HEIGHT

  if (abs < ONE / 2) {
    hoursOffset.value += e.deltaY
  } else {
    hoursOffset.value += Math.sign(e.deltaY) * ONE
  }

  // Нормализуем только offset, не трогаем массив
  normalizeOffsetOnly(hoursOffset, HOURS_ARRAY.length)

  // Вычисляем значение напрямую из offset
  selectedHour.value = computeSelectedValueFromOffset(hoursOffset.value, hoursContainer.value,
    HOURS_ARRAY, 'hours')

  if (hoursWheelTimer) {
    clearTimeout(hoursWheelTimer)
  }
  hoursWheelTimer = setTimeout(() => snapHours(), SNAP_DEBOUNCE)
}

/**
 * Обработчик колесика мыши для скроллера минут
 *
 * Назначение:
 * - Аналогичен onHoursWheel но для минутного скроллера
 * - Использует тот же алгоритм с учетом шага минут
 */
const onMinutesWheel = (e: WheelEvent) => {
  e.preventDefault()

  const abs = Math.abs(e.deltaY)
  const ONE = OPTION_HEIGHT

  if (abs < ONE / 2) {
    minutesOffset.value += e.deltaY
  } else {
    minutesOffset.value += Math.sign(e.deltaY) * ONE
  }

  normalizeOffsetOnly(minutesOffset, MINUTES_ARRAY.length)

  selectedMinute.value = computeSelectedValueFromOffset(minutesOffset.value, minutesContainer.value,
    MINUTES_ARRAY, 'minutes')

  if (minutesWheelTimer) {
    clearTimeout(minutesWheelTimer)
  }
  minutesWheelTimer = setTimeout(() => snapMinutes(), SNAP_DEBOUNCE)
}

/**
 * Обработчики жестов перетаскивания для часов
 *
 * Назначение:
 * - Поддержка touch и mouse drag жестов
 * - Плавное перемещение скроллера при перетаскивании
 * - Корректное завершение жеста с "прилипанием"
 */
const onHoursPointerDown = (e: PointerEvent) => {
  ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  hPointerActive = true
  hLastY = e.clientY
}

/**
 * Обработчики жестов перетаскивания для часов
 *
 * Назначение:
 * - Поддержка touch и mouse drag жестов
 * - Плавное перемещение скроллера при перетаскивании
 * - Корректное завершение жеста с "прилипанием"
 */
const onHoursPointerMove = (e: PointerEvent) => {
  if (!hPointerActive) {
    return
  }
  const dy = e.clientY - hLastY

  hLastY = e.clientY
  hoursOffset.value += dy

  normalizeOffsetOnly(hoursOffset, HOURS_ARRAY.length)
  selectedHour.value = computeSelectedValueFromOffset(hoursOffset.value, hoursContainer.value,
    HOURS_ARRAY, 'hours')
}

/**
 * Обработчики жестов перетаскивания для часов
 *
 * Назначение:
 * - Поддержка touch и mouse drag жестов
 * - Плавное перемещение скроллера при перетаскивании
 * - Корректное завершение жеста с "прилипанием"
 */
const onHoursPointerUp = () => {
  hPointerActive = false
  snapHours()
}

/**
 * Обработчики жестов перетаскивания для минут
 *
 * Назначение:
 * - Аналогичны часовым обработчикам но для минутного скроллера
 */
const onMinutesPointerDown = (e: PointerEvent) => {
  ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  mPointerActive = true
  mLastY = e.clientY
}

/**
 * Обработчики жестов перетаскивания для минут
 *
 * Назначение:
 * - Аналогичны часовым обработчикам но для минутного скроллера
 */
const onMinutesPointerMove = (e: PointerEvent) => {
  if (!mPointerActive) {
    return
  }
  const dy = e.clientY - mLastY

  mLastY = e.clientY
  minutesOffset.value += dy

  normalizeOffsetOnly(minutesOffset, MINUTES_ARRAY.length)
  selectedMinute.value = computeSelectedValueFromOffset(minutesOffset.value, minutesContainer.value,
    MINUTES_ARRAY, 'minutes')
}

/**
 * Обработчики жестов перетаскивания для минут
 *
 * Назначение:
 * - Аналогичны часовым обработчикам но для минутного скроллера
 */
const onMinutesPointerUp = () => {
  mPointerActive = false
  snapMinutes()
}

/**
 * Анимация смещения скроллера
 *
 * Назначение:
 * - Плавное перемещение скроллера к целевому значению
 * - Использование easing функции для натурального движения
 *
 * Входные данные:
 * - refObj: { value: number } - реактивная ссылка на смещение
 * - to: number - целевое значение смещения
 * - duration: number - длительность анимации
 * - cb: Function - callback по завершении анимации
 */
function animateOffset(refObj: { value: number }, to: number, duration = 150, cb?: () => void) {
  const from = refObj.value; const start = performance.now()
  function step(now: number) {
    const t = Math.min(1, (now - start) / duration)
    const ease = 1 - (1 - t) ** 3

    refObj.value = from + (to - from) * ease
    if (t < 1) {
      requestAnimationFrame(step)
    } else {
      refObj.value = to; cb?.()
    }
  }
  requestAnimationFrame(step)
}

/**
 * Функция "прилипания" для скроллера часов
 *
 * Назначение:
 * - Центрирование ближайшего значения часа после жеста
 * - Плавная анимация к целевому положению
 * - Обновление выбранного значения после анимации
 */
function snapHours() {
  const c = hoursContainer.value; if (!c) {
    return
  }

  const center = containerCenterOffset(c)
  const rawIndex = (-hoursOffset.value + center) / OPTION_HEIGHT
  const nearest = Math.round(rawIndex)
  const target = -(nearest * OPTION_HEIGHT) + center

  animateOffset(hoursOffset, target, 160, () => {
    // После анимации нормализуем offset в диапазон средней копии для бесконечного скроллинга
    normalizeOffsetOnly(hoursOffset, HOURS_ARRAY.length)

    // Вычисляем финальное значение
    selectedHour.value = computeSelectedValueFromOffset(hoursOffset.value, hoursContainer.value,
      HOURS_ARRAY, 'hours')
  })
}

/**
 * Функция "прилипания" для скроллера минут
 *
 * Назначение:
 * - Аналогична snapHours но для минутного скроллера
 * - Учет шага минут при центрировании
 */
function snapMinutes() {
  const c = minutesContainer.value; if (!c) {
    return
  }

  const center = containerCenterOffset(c)
  const rawIndex = (-minutesOffset.value + center) / OPTION_HEIGHT
  const nearest = Math.round(rawIndex)
  const target = -(nearest * OPTION_HEIGHT) + center

  animateOffset(minutesOffset, target, 160, () => {
    // После анимации нормализуем offset в диапазон средней копии для бесконечного скроллинга
    normalizeOffsetOnly(minutesOffset, MINUTES_ARRAY.length)

    // Вычисляем финальное значение
    selectedMinute.value = computeSelectedValueFromOffset(minutesOffset.value, minutesContainer.value,
      MINUTES_ARRAY, 'minutes')
  })
}

/**
 * Центрирование значения в скроллере
 *
 * Назначение:
 * - Программное установление позиции скроллера для заданного значения
 * - Используется при инициализации и внешних изменениях
 *
 * Входные данные:
 * - value: number - значение для центрирования
 * - container: HTMLElement | null - контейнер скроллера
 * - offsetRef: { value: number } - реактивная ссылка на смещение
 * - type: 'hours' | 'minutes' - тип значения
 */
function centerValueInList(value: number, container: HTMLElement | null, offsetRef: { value: number }, type: 'hours' | 'minutes') {
  if (!container) {
    return
  }
  const center = containerCenterOffset(container)

  // Находим индекс в базовом массиве
  const baseArray = type === 'hours'
    ? HOURS_ARRAY
    : MINUTES_ARRAY

  const idx = baseArray.indexOf(value)

  offsetRef.value = -((idx === -1 ? 0 : ((type === 'hours' ? 24 : baseArray.length) + idx)) * OPTION_HEIGHT) + center

  // Нормализуем offset после установки
  const arrLen = type === 'hours' ? HOURS_ARRAY.length : MINUTES_ARRAY.length

  normalizeOffsetOnly(offsetRef, arrLen)
}

/**
 * Сохранение выбранной даты и времени
 *
 * Назначение:
 * - Формирование итогового объекта Date с выбранными значениями
 * - Эмитинг событий обновления и сохранения
 * - Валидация и нормализация временных значений
 */
const saveDateTime = () => {
  if (!selectedDate.value) {
    return
  }

  const d = new Date(selectedDate.value)

  d.setHours(selectedHour.value)
  d.setMinutes(selectedMinute.value)
  d.setSeconds(0)
  d.setMilliseconds(0)

  emit('update:modelValue', d)
  emit('save', d)
}

/**
 * Финальное datetime, собранное из выбранных даты+часа+минуты.
 * Используется для проверки диапазона относительно onlyPastDate/onlyFutureDate
 * (часы/минуты VDatePicker не контролирует, поэтому ограничение нужно тут).
 */
const composedDateTime = computed<Date | null>(() => {
  if (!selectedDate.value) return null
  const d = new Date(selectedDate.value)

  d.setHours(selectedHour.value, selectedMinute.value, 0, 0)

  return d
})

/* True, если собранное datetime нарушает ограничение onlyPastDate/onlyFutureDate
   (например выбран сегодня + часы в будущем при onlyPastDate). */
const isOutOfRange = computed<boolean>(() => {
  const d = composedDateTime.value
  if (!d) return false
  const now = Date.now()
  if (props.onlyPastDate && d.getTime() > now) return true
  if (props.onlyFutureDate && d.getTime() < now) return true

  return false
})

/**
 * Вычисляемое свойство индекса выбранного часа
 *
 * Назначение:
 * - Определение позиции выбранного часа в массиве значений
 * - Используется для отладки и визуального выделения
 *
 * Возвращает:
 * - number: индекс выбранного часа в массиве
 */
const selectedHourIndex = computed(() => {
  const value = computeSelectedValueFromOffset(hoursOffset.value, hoursContainer.value, HOURS_ARRAY, 'hours')
  return HOURS_ARRAY.indexOf(value)
})

/**
 * Вычисляемое свойство индекса выбранной минуты
 *
 * Назначение:
 * - Определение позиции выбранной минуты в массиве значений
 * - Учет шага минут при расчете индекса
 *
 * Возвращает:
 * - number: индекс выбранной минуты в массиве
 */
const selectedMinuteIndex = computed(() => {
  const value = computeSelectedValueFromOffset(minutesOffset.value, minutesContainer.value, MINUTES_ARRAY, 'minutes')
  return MINUTES_ARRAY.indexOf(value)
})

/**
 * Вотчер для синхронизации с внешними изменениями modelValue
 *
 * Назначение:
 * - Реакция на изменения modelValue извне
 * - Обновление внутренних состояний и перецентрирование скроллеров
 * - Обеспечение согласованности с родительским компонентом
 */
watch(() => props.modelValue, nv => {
  if (!nv) {
    return
  }
  selectedDate.value = new Date(nv)
  selectedHour.value = nv.getHours()
  selectedMinute.value = nv.getMinutes()
  nextTick(() => centerValueInList(selectedHour.value, hoursContainer.value, hoursOffset, 'hours'))
  nextTick(() => centerValueInList(selectedMinute.value, minutesContainer.value, minutesOffset, 'minutes'))
})

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * - Инициализацию CSS переменных и размеров
 * - Установку начальных значений даты и времени
 * - Настройку обработчиков событий жестов
 * - Обработку ресайза окна для перерасчета позиций
 */
onMounted(async () => {
  // прочитать CSS-переменные (опция/видимые строки)
  readCssVars(hoursContainer.value || (document.documentElement as HTMLElement))

  if (!props.modelValue) {
    const now = new Date()

    selectedDate.value = now
    selectedHour.value = now.getHours()
    selectedMinute.value = Math.floor(now.getMinutes() / props.minuteStep) * props.minuteStep
  }

  await nextTick()

  // центрируем текущие значения (offset задаёт положение внутреннего блока, без манипуляций с DOM-стилями)
  if (hoursContainer.value) {
    const center = containerCenterOffset(hoursContainer.value)
    const idx = HOURS_ARRAY.indexOf(selectedHour.value)

    hoursOffset.value = -((idx === -1 ? 0 : 24 + idx) * OPTION_HEIGHT) + center
    normalizeOffsetOnly(hoursOffset, HOURS_ARRAY.length)
  }
  if (minutesContainer.value) {
    const center = containerCenterOffset(minutesContainer.value)
    const idx = MINUTES_ARRAY.indexOf(selectedMinute.value)

    minutesOffset.value = -((idx === -1 ? 0 : (MINUTES_ARRAY.length + idx)) * OPTION_HEIGHT) + center
    normalizeOffsetOnly(minutesOffset, MINUTES_ARRAY.length)
  }

  // слушатели
  if (hoursContainer.value) {
    hoursContainer.value.addEventListener('wheel', onHoursWheel, { passive: false })
    hoursContainer.value.addEventListener('pointerdown', onHoursPointerDown)
    hoursContainer.value.addEventListener('pointermove', onHoursPointerMove)
    hoursContainer.value.addEventListener('pointerup', onHoursPointerUp)
    hoursContainer.value.addEventListener('pointercancel', onHoursPointerUp)
    hoursContainer.value.addEventListener('pointerleave', onHoursPointerUp)
  }
  if (minutesContainer.value) {
    minutesContainer.value.addEventListener('wheel', onMinutesWheel, { passive: false })
    minutesContainer.value.addEventListener('pointerdown', onMinutesPointerDown)
    minutesContainer.value.addEventListener('pointermove', onMinutesPointerMove)
    minutesContainer.value.addEventListener('pointerup', onMinutesPointerUp)
    minutesContainer.value.addEventListener('pointercancel', onMinutesPointerUp)
    minutesContainer.value.addEventListener('pointerleave', onMinutesPointerUp)
  }

  // при ресайзе высота контейнера меняется автоматически (CSS), но нужно пересчитать center offset и поправить offset
  window.addEventListener('resize', () => {
    // повторно читаем CSS (вдруг изменили переменные) и корректируем оффсеты
    readCssVars(hoursContainer.value || (document.documentElement as HTMLElement))

    // Перецентрируем текущие значения
    if (hoursContainer.value) {
      centerValueInList(selectedHour.value, hoursContainer.value, hoursOffset, 'hours')
    }

    if (minutesContainer.value) {
      centerValueInList(selectedMinute.value, minutesContainer.value, minutesOffset, 'minutes')
    }
  })
})
</script>

<template>
  <div
    class="datetime-picker datetime-picker-overlay"
    :class="xs ? 'd-flex flex-column pa-0' : 'd-flex pa-6'"
  >
    <div
      class="picker-container"
      :class="xs ? 'd-flex flex-column flex-grow-1' : 'd-flex flex-grow-1'"
    >
      <div
        class="date-picker-section"
        :class="xs ? 'w-100' : 'flex-grow-1'"
      >
        <VDatePicker
          v-model="selectedDate"
          weekday-format="short"
          hide-header
          :masks="{ input: 'DD.MM.YYYY' }"
          :min="minDate"
          :max="maxDate"
        />
      </div>

      <div
        class="time-picker-section"
        :class="xs ? 'w-100 mt-4' : 'flex-shrink-0'"
      >
        <div class="time-header text-center mb-2">
          Время
        </div>

        <div
          class="time-selectors"
          :class="xs ? 'justify-center' : ''"
        >
          <!-- центральная зона — визуально выделяет ровно 1 опцию (CSS) -->
          <div
            class="center-highlight"
            aria-hidden="true"
          />

          <!-- ЧАСЫ -->
          <div
            class="time-column"
            :class="xs ? 'mx-2' : ''"
          >
            <div
              ref="hoursContainer"
              class="time-scroll single-loop"
              role="listbox"
              aria-label="Часы"
            >
              <div
                class="time-inner"
                :style="{ transform: `translateY(${hoursOffset}px)` }"
              >
                <template
                  v-for="rep in 3"
                  :key="rep"
                >
                  <div
                    v-for="(h) in hours"
                    :key="`${rep}-${h}`"
                    class="time-option"
                    :class="{ 'is-active': h === selectedHour }"
                    @click.prevent
                  >
                    {{ String(h).padStart(2, '0') }}
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- МИНУТЫ -->
          <div
            class="time-column"
            :class="xs ? 'mx-2' : ''"
          >
            <div
              ref="minutesContainer"
              class="time-scroll single-loop"
              role="listbox"
              aria-label="Минуты"
            >
              <div
                class="time-inner"
                :style="{ transform: `translateY(${minutesOffset}px)` }"
              >
                <template
                  v-for="rep in 3"
                  :key="rep"
                >
                  <div
                    v-for="(m) in minutes"
                    :key="`${rep}-${m}`"
                    class="time-option"
                    :class="{ 'is-active': m === selectedMinute }"
                    @click.prevent
                  >
                    {{ String(m).padStart(2, '0') }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <VBtn
      color="primary"
      block
      class="mt-2 save-date-button"
      :class="{ 'mb-3 mx-2': xs }"
      :disabled="isOutOfRange"
      @click="saveDateTime"
    >
      Сохранить
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
.datetime-picker {
  --option-height: 40px;    /* высота опции  */

  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  overflow: hidden;
  max-height: 500px;
}

.picker-container {
  display: flex;
  flex: 1;
  min-height: 0;
}

.date-picker-section {
  flex: 1;
  border-right: 1px solid rgb(var(--v-theme-text-primary), var(--v-custom-opacity));;
}

.v-date-picker-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.time-picker-section {
  min-width: 7.5rem;
  display: flex;
  flex-direction: column;
  padding-inline: 1rem;
}

.time-selectors {
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
}

.time-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.time-scroll {
  overflow: hidden;
  user-select: none;
  touch-action: none;
  display: block;
  box-sizing: border-box;
}

.time-inner {
  display: flex;
  flex-direction: column;
  will-change: transform;
  position: relative;
  z-index: 3;
}

/* каждая опция */
.time-option {
  height: var(--option-height);
  line-height: var(--option-height);
  text-align: center;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  user-select: none;
  margin: 0;
  box-sizing: border-box;
  color: rgb(var(--v-theme-text-secondary), var(--v-high-emphasis-opacity));
}

/* Центральная область */
.center-highlight {
  position: absolute;
  top: calc(50% - (var(--option-height) / 2));
  left: 2px;
  right: 6px;
  height: var(--option-height);
  background: rgb(var(--v-theme-primary));
  border-radius: 8px;
  pointer-events: none;
  z-index: 2;
  width: 100%;
}

.time-option.is-active {
  color: rgb(var(--v-theme-paper));
}

/* Стили для надписи "Время" */
.time-header {
  color: rgb(var(--v-theme-text-secondary), var(--v-high-emphasis-opacity));
}
</style>

<style>
@media (max-width: 600px) {
  .v-overlay__content:has(.datetime-picker-overlay) {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90vw !important;
    max-width: 400px !important;
  }

  .datetime-picker {
    max-height: 80vh !important;
    overflow-y: auto !important;

  }

  /* ТОЛЬКО ФИКС ДЛЯ SPACER - остальное не трогаем */
  .v-date-picker-controls .v-spacer {
    display: none !important;
  }

  .v-date-picker-month {
    padding: 0 !important;
  }

  /* Уменьшаем высоту барабанов времени для мобилок */
  .time-picker-section {
    min-height: auto !important;
    padding-inline: 0.5rem !important;
    margin-top: 0 !important;
    margin-bottom: 5px;
  }

  .time-scroll {
    height: 80px !important;
  }

  .center-highlight {
    height: 30px !important;
    top: calc(50% - 15px) !important;
  }
  .time-header {
    margin-bottom: 0;
  }
  .time-option {
    height: 30px !important;
    line-height: 30px !important;
    font-size: 12px !important;
  }

  .datetime-picker {
    --option-height: 30px !important;
  }
}
</style>
